import AuthenticationType from './AuthenticationType';
import Endpoints from './Endpoints';
import HTTPMethods from './HTTPMethods';
import VerifyMFADetails from './objects/VerifyMFADetails';
import RequestDispatcher from './RequestDispatcher';

const FormURLEncodedHeader = 'application/x-www-form-urlencoded;charset=UTF-8';

class FBAPI extends RequestDispatcher {
    instrumentDetails = (instrumentId) => this.dispatchRequest({
        url: Endpoints.instrumentDetails(instrumentId),
    });

    suggestedResearch = (
        accountId,
        assetClass,
        currencyCode,
        includeWatchlist,
        includeInvestmentTracker,
    ) => {
        let params = '?';
        if (accountId) params += `accountId=${accountId}`;
        if (assetClass) params += `&assetClass=${assetClass}`;
        if (currencyCode) params += `&currencyCode=${currencyCode}`;
        if (includeWatchlist) params += `&includeWatchlist=${includeWatchlist}`;
        if (includeInvestmentTracker) params += `&includeTracker=${includeInvestmentTracker}`;

        return this.dispatchRequest({
            url: Endpoints.suggestedResearch(params),
        });
    };

    homeMarketPerformance = () => this.dispatchRequest({
        url: Endpoints.homeMarketPerformance(),
        authenticatedRoute: false,
    });

    accounts = () => this.dispatchRequest({
        url: Endpoints.accounts(),
    });

    accountSummary = (accountNumber) => this.dispatchRequest({
        url: Endpoints.accountSummary(accountNumber),
    });

    accountAssets = (accountNumber, assetClass, currencyCode) => {
        let params = '?';
        if (assetClass) params += `assetClass=${assetClass}`;
        if (currencyCode) params += `&currencyCode=${currencyCode}`;

        return this.dispatchRequest({
            url: Endpoints.accountAssets(accountNumber, params),
        });
    };

    accountAssetsPDF = (accountNumber, assetClass, currencyCode, sortField, sortDir) => {
        let params = '?';
        if (assetClass != null) params += `assetClass=${assetClass}`;
        if (currencyCode != null) params += `&currencyCode=${currencyCode}`;
        if (sortField != null) params += `&sortField=${sortField}`;
        if (sortDir != null) params += `&sortDir=${sortDir}`;

        return this.dispatchRequest({
            url: Endpoints.accountAssetsPDF(accountNumber, params),
        });
    };

    accountPerformanceValues = (accountNumber, dateForm, interval) => {
        const params = `?dateFrom=${dateForm}&timeInterval=${interval}`;

        return this.dispatchRequest({        
            url: Endpoints.accountPerformanceValues(accountNumber, params),
        });
};

    setupEmailMFA = (data) => this.dispatchRequest({
        url: Endpoints.setupEmailMFA,
        method: HTTPMethods.POST,
        body: data,
    });

    // Used to verify SMS or Email MFA
    verifyMFA = (data, isEmail = true) => this.dispatchRequest({
        url: isEmail ? Endpoints.verifyEmailMFA : Endpoints.verifySmsMFA(),
        method: HTTPMethods.POST,
        body: data,
        // parse response needs to be overwritten due to incorrect MFA code API returns
        // 401 status code. 401 in base parser will close the MFA process.
        parseResponse: ({ status, response }) => {
            if (status === 403) {
                window.location.reload();
                return;
            }

            if (status > 199 && status < 300) {
                return response;
            }

            // if technical message comes back as MFA_REQUIRED we need to return the data
            // in the error the data contains a new security header to re-try the
            // challenge response again.
            if (response.technicalMessage === 'MFA_REQUIRED') {
                const details = new VerifyMFADetails(response);

                return Promise.reject(details);
            }

            return this.rejectRequest(response);
        },
    });

    setupSmsMFA = (data) => this.dispatchRequest({
        url: Endpoints.setupSmsMFA(),
        method: HTTPMethods.POST,
        body: data,
    });

    checkPassword = (headers, data) => this.dispatchRequest({
        url: Endpoints.checkPassword,
        headers,
        method: HTTPMethods.POST,
        body: data,
    });

    setupAuthenticatorMFA = (securityId) => {
        const params = `?SecurityID=${securityId}`;

        return this.dispatchRequest({
            url: Endpoints.setupAuthenticatorMFA(params),
        });
    };

    verifyAuthenticatorMFA = (data) => this.dispatchRequest({
        url: Endpoints.verifyAuthenticatorMFA,
        method: HTTPMethods.POST,
        body: data,
    });

    removeUserMFA = (params) => this.dispatchRequest({
        url: Endpoints.removeUserMFA(params),
        method: HTTPMethods.DELETE,
    });

    getNCAccountStatus = (params) => this.dispatchRequest({
        url: Endpoints.getNCAccountStatus(params),
    });

    getNCAccounts = (params) => this.dispatchRequest({
        url: Endpoints.getNCAccounts(params),
    });

    getNCAccountSummary = (accountNumber) => this.dispatchRequest({
        url: Endpoints.getNCAccountSummary(accountNumber),
    });

    getNCAccountAssets = (accountNumber) => this.dispatchRequest({
        url: Endpoints.getNCAccountAssets(accountNumber),
    });

    getInstruments = (search, limit) => this.dispatchRequest({
        url: Endpoints.getInstruments(`?search=${search}&limit=${limit}`),
        authenticatedRoute: false,
    });

    getWatchlist = () => this.dispatchRequest({
        url: Endpoints.watchlist(),
    });

    removeWatchlistItems = (instrumentIds) => this.dispatchRequest({
        url: Endpoints.watchlist(),
        method: HTTPMethods.DELETE,
        body: { instrumentIds: instrumentIds },
    });

    addWatchlistItems = (instrumentIds) => this.dispatchRequest({
        url: Endpoints.watchlist(),
        method: HTTPMethods.POST,
        body: instrumentIds,
    });

    getUser = () => this.dispatchRequest({
        url: Endpoints.user(),
        method: HTTPMethods.GET,
    });

    updateUser = (data) => this.dispatchRequest({
        url: Endpoints.user(),
        method: HTTPMethods.PATCH,
        body: data,
        headers: { 'x-requested-with': 'XMLHttpRequest' },
    });

    saveEnquiry = (data) => this.dispatchRequest({
        url: Endpoints.saveEnquiry(),
        method: HTTPMethods.POST,
        body: data,
        authenticationType: AuthenticationType.hmac,
    });
}

export default new FBAPI();
export { FormURLEncodedHeader };
